import { useState } from 'react';
import { apiClient } from '@config/api';
import { TransactionLimit } from './useGetLimitsBySegment';

interface AlertMessage {
  title: string;
  description: string;
  variant: 'error' | 'success';
}

interface Status {
  loading: boolean;
  alertMessage: AlertMessage;
  success: boolean;
  error: boolean;
}

type MessageType = {
  [key: string]: AlertMessage;
};

/**
 * Delete limit state hook
 *
 * @remarks
 * this hook handles the screen status relationated to the service endpoint and response
 */
const useDeleteLimit = ({ handleRefresh }: { handleRefresh: () => void }) => {
  const MESSAGES: MessageType = {
    SUCCESS: {
      title: 'LIMITS:DELETE_MODAL:SUCCESS:TITLE',
      description: '',
      variant: 'success',
    },
  } as const;

  const [status, setStatus] = useState<Status>({
    loading: false,
    success: false,
    error: false,
    alertMessage: MESSAGES.SUCCESS,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [limitToDelete, setLimitToDelete] = useState<TransactionLimit | null>(null);
  /**
   * Function to set the error state.
   * @param {boolean} error - The error state.
   */
  const setIsError = (error: boolean) => {
    setStatus((prevState) => ({ ...prevState, error }));
  };

  /**
   * Function to set the sucess state.
   * @param {boolean} success - The success state.
   */
  const setIsSuccess = (success: boolean) => {
    setStatus((prevState) => ({ ...prevState, success }));
  };

  /**
   * Service for editing a limit
   * @param body - The request body.
   */
  async function deleteLimitDetail(id: number) {
    const url = `/v3/partner/limit/${id}`;

    const response = await apiClient.delete(url);
    return response.data;
  }

  /**
   * Edit limit service handler
   * @async
   * @param body - request payload
   */
  async function submit() {
    try {
      if (limitToDelete?.id === undefined) {
        throw new Error('Limit ID is undefined');
      }
      setStatus((prevState) => ({ ...prevState, loading: true }));
      await deleteLimitDetail(limitToDelete?.id);
      handleRefresh();
      setStatus({
        loading: false,
        error: false,
        success: true,
        alertMessage: MESSAGES.SUCCESS,
      });
    } finally {
      setIsModalOpen(false);
    }
  }
  /**
   *  Sets the choosen limit and opens the confirmation modal
   * @param id
   */
  const handlePreSubmit = (limit: TransactionLimit) => {
    setLimitToDelete(limit);
    setIsModalOpen(true);
  };
  const { loading: isLoading, success: isSuccess, error: isError, alertMessage } = status;

  return {
    isLoading,
    isSuccess,
    isError,
    alertMessage,
    setIsSuccess,
    setIsError,
    submit,
    isModalOpen,
    setIsModalOpen,
    limitToDelete,
    handlePreSubmit,
  };
};

export default useDeleteLimit;
